import FormField from './FormField';
import { useEffect } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

const areFieldValuesEqual = (a, b) => {
  // Simple equality check, can be replaced with a util if needed
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
};

const HasManyFieldGroup = ({ fieldGroup, deleteFieldGroup }) => {
  const minRows = fieldGroup.minRows ?? 1;
  const maxRows = fieldGroup.maxRows ?? 999;
  const { values } = useFormState();
  const form = useForm();

  // Get the array of rows for this group from form state
  const rows = values[fieldGroup.name] ?? [];
  // Count rows that are not soft-deleted
  const visibleRows = rows.filter((row) => !row?.isDeleted);
  const canAdd = visibleRows.length < maxRows;
  const canDelete = visibleRows.length > minRows;
  const addText = fieldGroup.addButtonText || `Add ${fieldGroup.headerText}`;

  // Ensure at least one empty row exists if there are no visible rows
  useEffect(() => {
    if (visibleRows.length === 0) {
      form.change(fieldGroup.name, [{}]);
    }
  }, [fieldGroup.name, visibleRows.length, form]);

  // Hide/show logic
  const compareFieldValue = (fieldName, compareValue) => {
    const val = values[fieldName];
    return areFieldValuesEqual(val, compareValue);
  };
  if (fieldGroup.hideExpression) {
    let rules = [];
    try {
      rules = JSON.parse(fieldGroup.hideExpression);
    } catch {
      // Ignore invalid hideExpression JSON and show the field group.
    }
    if (rules && typeof rules.length !== 'undefined') {
      for (let i = 0, ii = rules.length; i < ii; i++) {
        if (typeof rules[i].fieldName === 'undefined' || typeof rules[i].value === 'undefined') {
          continue;
        }
        if (compareFieldValue(rules[i].fieldName, rules[i].value)) {
          return null;
        }
      }
    }
  }

  return (
    <FieldArray name={fieldGroup.name}>
      {({ fields }) => {
        return (
          <section className="field-group-multi">
            {fields.map((name, index) => {
              const row = fields.value[index];
              if (row?.isDeleted) return null;
              // Set skipRequiredTest logic
              let skipRequiredTest = false;
              if (index + 1 > minRows) {
                skipRequiredTest = true;
                for (let i = 0, ii = fieldGroup.fields.length; i < ii; i++) {
                  const fieldName = fieldGroup.fields[i].fieldName;
                  const fieldValue = row ? row[fieldName] : undefined;
                  if (typeof fieldValue !== 'undefined' && fieldValue !== null && fieldValue !== false && fieldValue !== '') {
                    skipRequiredTest = false;
                    break;
                  }
                }
              }
              // Munge fieldGroup for this row
              let thisFieldGroup = JSON.parse(JSON.stringify(fieldGroup));
              thisFieldGroup.fields.forEach((field) => {
                field.fieldName = `${fieldGroup.name}[${index}].${field.fieldName}`;
                // State/message: if arrays, pick correct index
                if (Array.isArray(field.state)) field.state = field.state[index];
                if (Array.isArray(field.message)) field.message = field.message[index];
              });
              return (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <section key={index} className={`field-group${thisFieldGroup.cssClass ? ' ' + thisFieldGroup.cssClass : ''}`}>
                  <header>
                    <h4 className={thisFieldGroup.textCssClass}>{thisFieldGroup.headerText ? thisFieldGroup.headerText : ' '}</h4>
                    <div className="clearfix" />
                    {canDelete && (
                      <button
                        type="button"
                        className="btn btn-danger pull-right"
                        onClick={() => {
                          // Soft delete: set isDeleted
                          form.change(`${fieldGroup.name}[${index}].isDeleted`, true);
                          if (typeof deleteFieldGroup === 'function') {
                            deleteFieldGroup(fieldGroup, index);
                          }
                        }}
                      >
                        <span className="fa fa-times me-2" />
                        Delete
                      </button>
                    )}
                  </header>
                  {thisFieldGroup.fields.map((field) => (
                    <FormField
                      key={field.fieldName}
                      data={field}
                      skipRequiredTest={skipRequiredTest}
                      parent={fieldGroup}
                      hasManyRow={index}
                    />
                  ))}
                </section>
              );
            })}
            {canAdd && (
              <footer>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    fields.push({});
                  }}
                >
                  <span className="glyphicon glyphicon-plus" />
                  {addText}
                </button>
              </footer>
            )}
          </section>
        );
      }}
    </FieldArray>
  );
};

export default HasManyFieldGroup;
